import * as React from 'react'

import { createDataSourceDefinition } from '@factorialco/f0-react'
import { AttendanceTimesheetMetaDocument } from 'generated/resources/graphql'
import moment from 'moment'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import { MetaContext, MetaValue } from 'modules/core/lib/navigation/routes'
import { ResourceMeta } from 'modules/core/lib/navigation/types'

import { TimesheetEmployeeHeader } from 'modules/attendance/components/TimesheetEmployeeHeader'
import { NavigationParams } from 'modules/attendance/lib/types'
import EmployeeUtils from 'modules/attendance/lib/utils/EmployeeUtils'

export const buildTimesheetMeta = (
  { watchQuery, params, session }: MetaContext,
  navigation: Navigation<NavigationPaths>
): MetaValue<ResourceMeta> => {
  const employeeId = Number(params.employeeId)
  const companyId = session.company.id

  const today = moment()
  const listPath = navigation.timeTracking.list.periodTypeYearMonthDay.fullPath({
    periodType: params.periodType ?? 'monthly',
    year: Number(params.year ?? today.format('YYYY')),
    month: Number(params.month ?? today.format('MM')),
    day: Number(params.day ?? today.format('DD')),
  })

  return watchQuery({
    query: AttendanceTimesheetMetaDocument,
    fetchPolicy: 'cache-first',
    variables: {
      companyId,
      employeeId,
      referenceOn: moment().format('YYYY-MM-DD'),
    },
  }).map(({ data }) => {
    const currentEmployee = data.attendance.current
    const navigationParams: NavigationParams = {
      employeeId: params.employeeId ?? '',
      periodType: params.periodType ?? '',
      year: params.year ?? '',
      month: params.month ?? '',
      day: params.day ?? '1',
    }

    const activeEmployees = data.attendance.all
      .filter((employee) => EmployeeUtils.isActiveInPeriod(employee.terminatedOn, navigationParams))
      .sort((a, b) => a.access.firstName.localeCompare(b.access.firstName))

    if (!currentEmployee) {
      return { title: '' }
    }

    const currentEmployeeIndex = activeEmployees.findIndex((employee) => employee.id === employeeId)
    const previousEmployee =
      currentEmployeeIndex > 0 ? activeEmployees[currentEmployeeIndex - 1] : undefined
    const nextEmployee =
      currentEmployeeIndex < activeEmployees.length - 1
        ? activeEmployees[currentEmployeeIndex + 1]
        : undefined
    const referenceContract = data.contracts.referenceContractsConnection.nodes[0]

    const hasTimeTrackingAnnualisationDailyValue =
      !!data.companies.featureAvailabilitiesConnection.nodes?.some(
        (feature) => feature.id === 'time_tracking_annualisation_daily_value' && feature.available
      )

    return {
      title: currentEmployee.access.fullName,
      description: i18n.t('time_tracking.html_meta.timesheet'),
      path: listPath,
      headerComponent: (
        <TimesheetEmployeeHeader
          employee={currentEmployee}
          referenceContract={referenceContract}
          hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
        />
      ),
      siblingsNavigation:
        activeEmployees.length <= 1
          ? undefined
          : {
              previous: previousEmployee
                ? {
                    title: i18n.t('time_tracking.navigation.previous', {
                      name: previousEmployee.access.fullName,
                    }),
                    url: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath(
                      {
                        ...navigationParams,
                        employeeId: previousEmployee.id,
                      }
                    ),
                  }
                : undefined,
              next: nextEmployee
                ? {
                    title: i18n.t('time_tracking.navigation.next', {
                      name: nextEmployee.access.fullName,
                    }),
                    url: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath(
                      {
                        ...navigationParams,
                        employeeId: nextEmployee.id,
                      }
                    ),
                  }
                : undefined,
              counter: {
                total: activeEmployees.length,
                current: currentEmployeeIndex + 1,
              },
              select: {
                url: (value: string) =>
                  navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath({
                    ...navigationParams,
                    employeeId: value,
                  }),
                source: createDataSourceDefinition({
                  dataAdapter: {
                    fetchData: async ({ search }) => {
                      const res = (activeEmployees || [])
                        .filter(
                          (employee) =>
                            !search ||
                            employee.access.fullName.toLowerCase().includes(search.toLowerCase())
                        )
                        .map((employee) => ({
                          label: employee.access.fullName,
                          value: employee.id.toString(),
                          avatar: {
                            type: 'person' as const,
                            firstName: employee.preferredName || employee.access.firstName,
                            lastName: employee.access.lastName,
                            src: buildUrl(employee.access?.avatar) || undefined,
                          },
                        }))

                      return Promise.resolve({
                        records: res,
                      })
                    },
                  },
                }),
                mapOptions: (options) => {
                  return options
                },
                value: employeeId.toString(),
              },
            },
    }
  })
}
